import React, { useState } from "react";
import { LocalShipping as ShippingIcon } from "@mui/icons-material";
import { Order } from "../../../types";
import { orderService } from "../../../services/orderService";
import { deliveryService } from "../../../services/deliveryService";
import { ConfirmChangeDialog } from "./ConfirmChangeDialog";
import { PickOption, QuickPickChip } from "./QuickPickChip";
import { Notify, useQuickEditSave } from "./useQuickEditSave";

const loadCouriers = async (): Promise<PickOption[]> =>
    (await deliveryService.getDeliveryServicesSimple(true)).map((s) => ({
        id: s.delivery_service_id,
        name: s.name,
        detail: s.type === "THIRD_PARTY" ? "Courier" : "Own delivery",
    }));

interface CourierPickerProps {
    orderId: string;
    courier: { id: string; name: string } | null;
    /** A consignment already booked with the old courier — changing doesn't move it. */
    consignmentId?: string | null;
    lockedReason?: string | null;
    onOrderChanged: (order: Order) => void;
    notify: Notify;
}

/**
 * An order's delivery service as a chip — click to pick another, confirm to
 * save. Used by the order drawer and the Delivery Planner cards.
 */
export const CourierPicker: React.FC<CourierPickerProps> = ({
    orderId,
    courier,
    consignmentId,
    lockedReason = null,
    onOrderChanged,
    notify,
}) => {
    // `pending` outlives `open` so the dialog keeps its text while it animates
    // closed. `from` is fixed at pick time — the order (and so `courier`)
    // refreshes before the dialog has finished closing.
    const [pending, setPending] = useState<{ option: PickOption; from: string } | null>(null);
    const [open, setOpen] = useState(false);
    const { saving, error, setError, save } = useQuickEditSave(orderId, onOrderChanged, notify);

    const confirm = async () => {
        if (!pending) return;
        const { option } = pending;
        const saved = await save(
            () => orderService.updateOrder(orderId, { delivery_service_id: option.id }),
            `Delivery service changed to ${option.name}`,
        );
        if (saved) setOpen(false);
    };

    return (
        <>
            <QuickPickChip
                icon={<ShippingIcon fontSize="small" />}
                value={courier}
                placeholder="Set courier"
                menuTitle="Delivery service"
                color="primary"
                loadOptions={loadCouriers}
                onPick={(option) => {
                    setError("");
                    setPending({ option, from: courier?.name ?? "None" });
                    setOpen(true);
                }}
                lockedReason={lockedReason}
            />
            <ConfirmChangeDialog
                open={open}
                title="Change delivery service?"
                rows={pending ? [{ label: "Delivery service", from: pending.from, to: pending.option.name }] : []}
                warnings={
                    consignmentId
                        ? [
                              `Consignment #${consignmentId} was created${courier ? ` with ${courier.name}` : ""}. ` +
                                  "Changing the delivery service here doesn't cancel or move it.",
                          ]
                        : []
                }
                error={error}
                saving={saving}
                onConfirm={confirm}
                onCancel={() => setOpen(false)}
            />
        </>
    );
};
